import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader } from './ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import axios from 'axios';
import { ChevronDown, ChevronUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Archive Strength Progress Bar - refined, heritage feel
const ArchiveStrengthBar = ({ strength, label, generations }) => {
  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-[0.2em]">
            Archive Strength
          </p>
          <p className="text-xl font-serif text-foreground mt-1 tracking-tight">
            {label}
          </p>
        </div>
        <div className="text-right">
          <span className="text-2xl font-serif text-[#C9A961]">{Math.round(strength)}</span>
          <span className="text-muted-foreground text-sm font-light">/100</span>
        </div>
      </div>
      
      {/* Progress bar - thin, elegant */}
      <div className="relative h-1 bg-secondary rounded-full overflow-hidden">
        <motion.div
          className="absolute inset-y-0 left-0 rounded-full"
          style={{ 
            background: 'linear-gradient(90deg, #8B7355 0%, #C9A961 50%, #D4B96A 100%)' 
          }}
          initial={{ width: 0 }}
          animate={{ width: `${strength}%` }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        />
      </div>
      
      {/* Generation depth - understated */}
      <p className="text-[11px] text-muted-foreground font-light">
        {generations} verified generation{generations !== 1 ? 's' : ''} connected
      </p>
    </div>
  );
};

// Preservation Alert Item - refined, no icons, text-focused
const AlertItem = ({ alert }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="py-2.5 border-b border-border/60 last:border-0"
    >
      <p className="text-[13px] text-muted-foreground leading-relaxed">
        {alert.message}
      </p>
      {alert.affected_names && alert.affected_names.length > 0 && (
        <p className="text-[11px] text-muted-foreground/70 mt-1.5 font-light">
          {alert.affected_names.slice(0, 3).join(' · ')}
          {alert.affected_names.length > 3 && ` · +${alert.affected_names.length - 3} more`}
        </p>
      )}
    </motion.div>
  );
};

// Milestone Item - heritage feel, minimal gold accent, no cartoon icons
const MilestoneItem = ({ milestone, index }) => {
  const isAchieved = milestone.achieved;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: index * 0.08, duration: 0.5 }}
      className={`relative py-4 border-b border-border/50 last:border-0 ${
        isAchieved ? '' : 'opacity-40'
      }`}
    >
      <div className="flex items-start gap-4">
        {/* Minimal gold marker for achieved */}
        <div className="flex-shrink-0 pt-1">
          {isAchieved ? (
            <div className="w-2 h-2 rounded-full bg-[#C9A961]" />
          ) : (
            <div className="w-2 h-2 rounded-full border border-muted-foreground/50" />
          )}
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <h4 className={`text-[13px] font-medium tracking-wide ${
            isAchieved ? 'text-foreground' : 'text-muted-foreground'
          }`}>
            {milestone.title}
          </h4>
          <p className={`text-[11px] mt-1 font-light leading-relaxed ${
            isAchieved ? 'text-muted-foreground' : 'text-muted-foreground/60'
          }`}>
            {milestone.description}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

// Main Legacy Preservation Component
const LegacyPreservation = ({ treeId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [alertsExpanded, setAlertsExpanded] = useState(true);
  const [milestonesExpanded, setMilestonesExpanded] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!treeId) return;
      
      setLoading(true);
      try {
        const response = await axios.get(`${API}/trees/${treeId}/legacy-preservation`);
        setData(response.data);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch legacy preservation data:', err);
        setError('Unable to load preservation data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [treeId]);

  if (loading) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="py-16">
          <div className="flex flex-col items-center justify-center text-muted-foreground">
            <div className="w-6 h-6 border border-border border-t-[#C9A961] rounded-full animate-spin mb-4" />
            <span className="text-[11px] uppercase tracking-[0.15em]">Analyzing archive</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="py-12 text-center text-muted-foreground">
          <p className="text-[12px]">{error || 'No data available'}</p>
        </CardContent>
      </Card>
    );
  }

  const achievedMilestones = data.milestones.filter(m => m.achieved);
  const pendingMilestones = data.milestones.filter(m => !m.achieved);

  return (
    <div className="space-y-3" data-testid="legacy-preservation">
      {/* Archive Strength Card */}
      <Card className="border-border/70 bg-card/95 shadow-none overflow-hidden">
        <CardHeader className="pb-0 pt-4 px-4">
          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-[0.18em]">
            Legacy Depth
          </p>
        </CardHeader>
        <CardContent className="pt-3 px-4 pb-4">
          <ArchiveStrengthBar 
            strength={data.archive_strength} 
            label={data.depth_label}
            generations={data.verified_generations}
          />
          
          {/* Quiet stats - understated */}
          <div className="grid grid-cols-3 gap-2 mt-5 pt-4 border-t border-border/60">
            <div className="text-center">
              <div className="text-base font-serif text-foreground">{data.total_individuals}</div>
              <div className="text-[9px] text-muted-foreground/90 mt-0.5 uppercase tracking-wider">Ancestors</div>
            </div>
            <div className="text-center border-x border-border/60">
              <div className="text-base font-serif text-foreground">{data.stories_recorded}</div>
              <div className="text-[9px] text-muted-foreground/90 mt-0.5 uppercase tracking-wider">Voices</div>
            </div>
            <div className="text-center">
              <div className="text-base font-serif text-foreground">{data.documents_attached}</div>
              <div className="text-[9px] text-muted-foreground/90 mt-0.5 uppercase tracking-wider">Records</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preservation Concerns - refined language */}
      {data.preservation_alerts.length > 0 && (
        <Collapsible open={alertsExpanded} onOpenChange={setAlertsExpanded}>
          <Card className="border-border/70 bg-card/95 shadow-none">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-secondary/30 transition-colors py-3 px-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-[0.15em]">
                      Preservation Concerns
                    </p>
                    <span className="text-[10px] text-muted-foreground">
                      {data.preservation_alerts.length}
                    </span>
                  </div>
                  {alertsExpanded ? (
                    <ChevronUp className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  )}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0 px-4 pb-3" data-testid="preservation-alerts">
                <AnimatePresence>
                  {data.preservation_alerts.map((alert) => (
                    <AlertItem key={alert.id} alert={alert} />
                  ))}
                </AnimatePresence>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      {/* Heritage Unlocked - milestones as discoveries, not achievements */}
      <Collapsible open={milestonesExpanded} onOpenChange={setMilestonesExpanded}>
        <Card className="border-border/70 bg-card/95 shadow-none">
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-secondary/30 transition-colors py-3 px-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-[0.15em]">
                    Heritage Unlocked
                  </p>
                  {achievedMilestones.length > 0 && (
                    <span className="text-[10px] text-[#C9A961]">
                      {achievedMilestones.length}
                    </span>
                  )}
                </div>
                {milestonesExpanded ? (
                  <ChevronUp className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                )}
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0 px-4 pb-2" data-testid="milestones">
              {/* Achieved - "Secured" language */}
              {achievedMilestones.length > 0 && (
                <div className="mb-4">
                  <p className="text-[9px] text-muted-foreground/70 uppercase tracking-[0.2em] mb-2 pb-2 border-b border-border/50">
                    Secured
                  </p>
                  {achievedMilestones.map((milestone, index) => (
                    <MilestoneItem key={milestone.id} milestone={milestone} index={index} />
                  ))}
                </div>
              )}
              
              {/* Pending - "Awaiting Discovery" */}
              {pendingMilestones.length > 0 && (
                <div>
                  <p className="text-[9px] text-muted-foreground/70 uppercase tracking-[0.2em] mb-2 pb-2 border-b border-border/50">
                    Awaiting Discovery
                  </p>
                  {pendingMilestones.slice(0, 3).map((milestone, index) => (
                    <MilestoneItem key={milestone.id} milestone={milestone} index={index} />
                  ))}
                  {pendingMilestones.length > 3 && (
                    <p className="text-[10px] text-muted-foreground/70 text-center py-3 font-light">
                      {pendingMilestones.length - 3} more awaiting
                    </p>
                  )}
                </div>
              )}
              
              {/* Empty state */}
              {achievedMilestones.length === 0 && pendingMilestones.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <p className="text-[11px] font-light">Your heritage awaits discovery</p>
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </div>
  );
};

export default LegacyPreservation;
